import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Typography,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationsContext';
import { getDashboardStats } from '../services/reportService';
import { getBrands, getBrandById } from '../services/brandService';
import { StatsCards, StatisticsPageSkeleton, CroKpiCards } from '../components/dashboard';
import StatisticsBrandTabs from '../components/dashboard/StatisticsBrandTabs';
import RecentReportsList from '../components/dashboard/RecentReportsList';
import StatisticsLifecycleCards from '../components/dashboard/StatisticsLifecycleCards';
import StatisticsDeepCharts from '../components/dashboard/StatisticsDeepCharts';
import StatisticsAdvancedCharts from '../components/dashboard/StatisticsAdvancedCharts';
import { BRAND } from '../theme/brandTheme';
import showNotification from '../components/common/Notification';

/** Full statistics & charts — separate from main dashboard report list */
const DashboardStatistics = () => {
  const { user } = useAuth();
  const { lastReportEvent } = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams();
  const brandId = searchParams.get('brand');
  const [stats, setStats] = useState(null);
  const [brandName, setBrandName] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userBrandIds, setUserBrandIds] = useState([]);
  const lastSocketEventRef = useRef(null);

  const userId = user?._id || user?.id;
  const userRole = user?.role;

  useEffect(() => {
    const newBrandIds = user?.brands ? user.brands.map((b) => b._id) : [];
    setUserBrandIds((current) => {
      if (
        current.length === newBrandIds.length &&
        current.every((id, i) => id === newBrandIds[i])
      ) {
        return current;
      }
      return newBrandIds;
    });
  }, [user]);

  // Brands for tab filter — admin/editor: all; user: assigned only
  useEffect(() => {
    const loadBrands = async () => {
      if (!user) return;
      try {
        if (userRole === 'admin' || userRole === 'editor') {
          const list = await getBrands();
          setBrands(list || []);
        } else {
          setBrands(user.brands || []);
        }
      } catch {
        setBrands([]);
      }
    };
    loadBrands();
  }, [user, userRole]);

  const brandTabIds = useMemo(() => brands.map((b) => b._id), [brands]);

  const handleBrandTabChange = useCallback(
    (nextBrandId) => {
      const next = new URLSearchParams(searchParams);
      if (nextBrandId) next.set('brand', nextBrandId);
      else next.delete('brand');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Drop invalid brand id from URL (e.g. after access change)
  useEffect(() => {
    if (!brandId || !brandTabIds.length) return;
    if (!brandTabIds.includes(brandId)) {
      handleBrandTabChange(null);
    }
  }, [brandId, brandTabIds, handleBrandTabChange]);

  const fetchStats = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);
        const brandIds = userRole === 'admin' ? [] : userBrandIds;
        const data = await getDashboardStats(brandIds, brandId || null);
        setStats(data);
        setError(null);

        if (brandId) {
          const fromList = brands.find((b) => b._id === brandId);
          if (fromList) {
            setBrandName(fromList.name);
          } else {
            const brand = await getBrandById(brandId);
            setBrandName(brand?.name || null);
          }
        } else {
          setBrandName(null);
        }
      } catch (err) {
        console.error('Error fetching statistics:', err);
        if (!silent) setError('İstatistikler yüklenirken bir hata oluştu.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [userRole, userBrandIds, brandId, brands],
  );

  useEffect(() => {
    if (lastReportEvent?.at) lastSocketEventRef.current = lastReportEvent.at;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    if (userBrandIds.length > 0 || userRole === 'admin') {
      fetchStats();
      return;
    }
    setLoading(false);
  }, [user, userId, userRole, userBrandIds, fetchStats]);

  useEffect(() => {
    if (!lastReportEvent) return;
    if (lastSocketEventRef.current === lastReportEvent.at) return;
    lastSocketEventRef.current = lastReportEvent.at;
    fetchStats({ silent: true });
    showNotification('İstatistikler güncellendi', 'info');
  }, [lastReportEvent, fetchStats]);

  if (loading) {
    return <StatisticsPageSkeleton />;
  }

  if (userBrandIds.length === 0 && userRole !== 'admin') {
    return (
      <Container maxWidth="lg">
        <Typography variant="h5" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          Henüz size atanmış bir marka bulunmamaktadır.
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Typography color="error" sx={{ mt: 4, mb: 2 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => fetchStats()}>
          Yeniden Dene
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Button
        component={RouterLink}
        to={brandId ? `/dashboard/brand/${brandId}` : '/dashboard'}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: 'none', color: BRAND.purple, fontWeight: 600 }}
      >
        {brandId ? 'Marka Sayfası' : 'Dashboard'}
      </Button>

      <Box sx={{ mb: 4 }}>
        <Chip
          label={brandId ? 'Marka Analizi' : 'Analiz'}
          size="small"
          sx={{ mb: 1.5, bgcolor: `${BRAND.purple}14`, color: BRAND.purple, fontWeight: 600 }}
        />
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 700, color: BRAND.purple, fontFamily: BRAND.fontFamily, mb: 1 }}
        >
          <BarChartIcon sx={{ mr: 1.5, verticalAlign: 'middle' }} />
          {brandName ? `${brandName} — İstatistikler` : 'İstatistikler ve Grafikler'}
        </Typography>
        <Typography variant="body1" sx={{ color: BRAND.textMuted, fontFamily: BRAND.fontFamily }}>
          {brandName
            ? `${brandName} markasına ait test dağılımı ve performans grafikleri.`
            : 'Test portföyünüzün durum dağılımı, performans metrikleri ve marka bazlı grafikler.'}
        </Typography>
      </Box>

      <StatisticsBrandTabs
        brands={brands}
        selectedBrandId={brandId}
        onChange={handleBrandTabChange}
      />

      <CroKpiCards
        croKpis={stats?.croKpis}
        user={user}
        hideLeaderboard={!!brandId}
      />

      <StatisticsLifecycleCards lifecycle={stats?.lifecycle} />

      <StatsCards stats={stats} user={user} standalone hideBrandChart={!!brandId} />

      <StatisticsDeepCharts charts={stats?.charts} />

      <StatisticsAdvancedCharts charts={stats?.charts} />

      <RecentReportsList reports={stats?.recentReports} />
    </Container>
  );
};

export default DashboardStatistics;
